import { motion } from "motion/react";
import { Linkedin, Mail, ArrowUpRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import authorImg from "@/assets/portrait.webp";
import { SITE, SOCIAL } from "@/lib/site";

export const AUTHOR_BIO =
  "Arif Khan is a Security Infrastructure & Building Technology Strategist with 23+ years of experience across Security Infrastructure, Perimeter Protection, Smart Automation, Luxury Home Cinema and Audio Video solutions. He has contributed to more than 1000+ projects and over 500km+ of perimeter security initiatives while leading strategic technology initiatives at Altima Global.";

type Variant = "full" | "compact";

export function AuthorCard({
  variant = "full",
  eyebrow = "About the Author",
  className = "",
}: {
  variant?: Variant;
  eyebrow?: string;
  className?: string;
}) {
  return (
    <motion.aside
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`relative overflow-hidden rounded-3xl glass border border-white/40 shadow-[0_30px_80px_-40px_rgba(21,101,192,0.45)] ${className}`}
      aria-label="About Arif Khan"
    >
      {/* subtle blue accent wash */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 pointer-events-none"
      />
      <div className="relative grid gap-8 p-8 md:p-10 md:grid-cols-[auto_1fr] md:items-center">
        <div className="relative shrink-0">
          <div
            aria-hidden
            className="absolute -inset-2 rounded-full bg-gradient-to-br from-primary/30 to-accent/20 blur-xl"
          />
          <div
            className={`relative overflow-hidden rounded-full border border-white/60 ring-1 ring-primary/20 ${
              variant === "compact" ? "h-20 w-20" : "h-28 w-28 md:h-32 md:w-32"
            }`}
          >
            <img
              src={authorImg}
              alt="Arif Khan — Security Infrastructure & Building Technology Strategist"
              width={256}
              height={256}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="min-w-0">
          <div className="eyebrow text-primary">{eyebrow}</div>
          <h3 className="mt-2 text-xl md:text-2xl font-semibold tracking-tight">
            Arif Khan
          </h3>
          <div className="mt-1 text-sm text-ink-soft">
            Security Infrastructure &amp; Building Technology Strategist
          </div>
          <p className="mt-4 text-sm md:text-[15px] text-ink-soft leading-relaxed">
            {AUTHOR_BIO}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={SOCIAL.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-medium text-primary-foreground hover:opacity-90 transition-opacity"
            >
              <Linkedin className="h-3.5 w-3.5" strokeWidth={1.8} />
              LinkedIn
              <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-border-strong px-5 py-2.5 text-xs font-medium text-foreground hover:bg-surface transition-colors"
            >
              <Mail className="h-3.5 w-3.5" strokeWidth={1.8} />
              Contact
            </Link>
            <span className="text-[11px] font-mono uppercase tracking-widest text-ink-soft/80">
              {SITE.role}
            </span>
          </div>
        </div>
      </div>
    </motion.aside>
  );
}
